import * as React from "react";
import {
  Button,
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Textarea,
  toast,
} from "@eduviaos/ui-kit";
import { Loader2, Plus } from "lucide-react";
import type { AddActivityPayload, AdmissionEnquiry } from "@eduviaos/shared";
import { useAddActivity } from "@/hooks/useAdmissions";

/**
 * AddActivityDialog — Phase D6.
 *
 * Single-purpose form for logging a new CRM activity. The
 * `activity_type` choices map to the BE `Literal[...]` union — system
 * types (`stage_changed`, `assigned`, `followup_set`) are NOT pickable
 * here because the BE auto-generates those when the corresponding
 * action fires.
 *
 * Type-aware metadata: when "Call" is selected the form surfaces a
 * `Duration (minutes)` field that gets stored under
 * `metadata.duration_seconds`. Other types stay simple — note only.
 *
 * Validation:
 *   - Note required, ≥ 3 chars (matches BE schema min_length).
 *   - Duration optional, non-negative integer.
 *
 * After success the parent's enquiry cache is updated by the mutation
 * hook itself; we just close the dialog.
 */

type UserType = AddActivityPayload["activity_type"];

const TYPE_OPTIONS: Array<{ value: UserType; label: string; help: string }> = [
  { value: "call", label: "Call", help: "A phone call with the parent or student." },
  { value: "email", label: "Email", help: "An email exchange — copy the subject + summary." },
  { value: "message", label: "Message", help: "WhatsApp / SMS / chat conversation." },
  { value: "note", label: "Note", help: "An internal note for the team — no contact made." },
  {
    value: "document_uploaded",
    label: "Document",
    help: "A document was received from the family.",
  },
  { value: "payment_received", label: "Payment", help: "A payment was received from the family." },
  {
    value: "scheduled",
    label: "Scheduled event",
    help: "Something is scheduled — visit, meeting, follow-through.",
  },
];

interface AddActivityDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  enquiry: AdmissionEnquiry;
}

export function AddActivityDialog({ open, onOpenChange, enquiry }: AddActivityDialogProps) {
  const mut = useAddActivity(enquiry.id);
  const [type, setType] = React.useState<UserType>("call");
  const [note, setNote] = React.useState("");
  const [durationMins, setDurationMins] = React.useState<string>("");
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (open) {
      setType("call");
      setNote("");
      setDurationMins("");
      setError(null);
    }
  }, [open]);

  const onSubmit = async () => {
    setError(null);
    const trimmed = note.trim();
    if (trimmed.length < 3) {
      setError("Note is required (at least 3 characters).");
      return;
    }
    const metadata: Record<string, unknown> = {};
    if (type === "call" && durationMins.trim()) {
      const mins = Number(durationMins);
      if (!Number.isFinite(mins) || mins < 0) {
        setError("Duration must be a non-negative number of minutes.");
        return;
      }
      metadata.duration_seconds = Math.round(mins * 60);
    }
    try {
      await mut.mutateAsync({
        activity_type: type,
        note: trimmed,
        metadata: Object.keys(metadata).length ? metadata : undefined,
      });
      toast.success(`Activity logged for ${enquiry.student_name}.`);
      onOpenChange(false);
    } catch (e) {
      setError((e as { message?: string } | null)?.message ?? "Couldn't save activity.");
    }
  };

  const helpText = TYPE_OPTIONS.find((o) => o.value === type)?.help ?? "";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Plus className="size-4" aria-hidden="true" />
            Log activity
          </DialogTitle>
          <DialogDescription>
            Add a touchpoint on {enquiry.student_name}'s enquiry. This appears in the activity
            timeline immediately and never can be edited or deleted (audit trail).
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="space-y-1.5">
            <Label htmlFor="activity-type">Type</Label>
            <Select value={type} onValueChange={(v) => setType(v as UserType)}>
              <SelectTrigger id="activity-type" data-testid="activity-type-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {TYPE_OPTIONS.map((o) => (
                  <SelectItem key={o.value} value={o.value}>
                    {o.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-muted-foreground text-[11px]">{helpText}</p>
          </div>

          {type === "call" && (
            <div className="space-y-1.5">
              <Label htmlFor="activity-duration">Duration (minutes, optional)</Label>
              <Input
                id="activity-duration"
                type="number"
                min={0}
                step={1}
                value={durationMins}
                onChange={(e) => setDurationMins(e.target.value)}
                placeholder="e.g. 7"
                data-testid="activity-duration-input"
              />
            </div>
          )}

          <div className="space-y-1.5">
            <Label htmlFor="activity-note">Note</Label>
            <Textarea
              id="activity-note"
              value={note}
              onChange={(e) => setNote(e.target.value.slice(0, 2000))}
              placeholder="What happened? Outcome, next step…"
              rows={4}
              data-testid="activity-note-input"
            />
            <p
              className={
                note.length >= 1900
                  ? "text-[11px] text-amber-600 dark:text-amber-400"
                  : "text-muted-foreground text-[11px]"
              }
            >
              {note.length}/2000 characters
            </p>
          </div>

          {error && (
            <p className="text-destructive text-xs" role="alert">
              {error}
            </p>
          )}
        </div>

        <DialogFooter>
          <DialogClose asChild>
            <Button variant="outline" size="sm" disabled={mut.isPending}>
              Cancel
            </Button>
          </DialogClose>
          <Button size="sm" onClick={onSubmit} disabled={mut.isPending} data-testid="activity-save">
            {mut.isPending ? (
              <Loader2 className="size-4 animate-spin" aria-hidden="true" />
            ) : (
              <Plus className="size-4" aria-hidden="true" />
            )}
            Log activity
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
